"use client";

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

import { reportSharedEntry, type ShareReportReason } from "@/lib/api/api-client";

import { ReportDialog } from "./report-dialog";

/**
 * Le signalement d'un partage reçu, depuis n'importe quelle surface.
 *
 * # Pourquoi un contexte, comme le retour d'action
 *
 * Pour la même raison, et elle est encore plus nette ici : le geste part d'un
 * menu à trois points, qui vit dans un popover **démonté à la fermeture**. Une
 * modale dont l'état vivrait dans ce menu disparaîtrait au moment précis où
 * elle doit s'ouvrir. Le formulaire doit donc survivre à son émetteur.
 *
 * Et il y a plusieurs émetteurs : une tuile de grille, une bande, une ligne de
 * playlist. Un état par surface aurait fait trois modales là où il n'en faut
 * qu'une.
 *
 * Monté par le layout de la bibliothèque, à côté des deux fournisseurs qui y
 * sont déjà.
 */

type ReportTarget = {
  readonly entryId: string;
  readonly pieceTitle: string;
};

type ReportShareContextValue = {
  /** Ouvre le formulaire pour cette entrée reçue par partage. */
  readonly requestReport: (target: ReportTarget) => void;
};

const ReportShareContext = createContext<ReportShareContextValue | null>(null);

export function ReportShareProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<ReportTarget | null>(null);

  const requestReport = useCallback((next: ReportTarget) => {
    setTarget(next);
  }, []);

  const value = useMemo<ReportShareContextValue>(() => ({ requestReport }), [requestReport]);

  return (
    <ReportShareContext.Provider value={value}>
      {children}

      {target !== null && (
        <ReportDialog
          open
          onClose={() => setTarget(null)}
          pieceTitle={target.pieceTitle}
          onSubmit={(reason: ShareReportReason, message: string | null) =>
            reportSharedEntry(target.entryId, reason, message)
          }
        />
      )}
    </ReportShareContext.Provider>
  );
}

/**
 * Rend `null` hors de la bibliothèque plutôt que de lever.
 *
 * Le menu de morceau est partagé avec des surfaces qui ne sont pas sous ce
 * fournisseur, et une entrée reçue par partage ne s'y affiche pas. Lever ferait
 * dépendre un menu générique d'un contexte que sa moitié des appelants n'a
 * aucune raison de monter.
 */
export function useReportShare(): ReportShareContextValue | null {
  return useContext(ReportShareContext);
}
